import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../config';
import { deletePlace } from '../services/places.service';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    published: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Published' },
    draft: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Draft' },
    archived: { bg: 'bg-white/5', text: 'text-white/40', label: 'Archived' }
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, placeName, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-sm p-6 animate-fade-in">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-red-400">warning</span>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">Delete Place</h3>
            <p className="text-white/50 mb-1">
              Are you sure you want to delete <strong className="text-white">"{placeName}"</strong>?
            </p>
            <p className="text-sm text-white/30 mt-2">
              This will also delete all images and data associated with this place.
            </p>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Preview Modal
const PreviewModal = ({ isOpen, onClose, place, onEdit }) => {
  if (!isOpen || !place) return null;

  const images = place.images || [];
  const keyInfo = place.keyInformation || place.key_information || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fade-in">
          
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0a0a0f] border-b border-white/5 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-medium text-white">{place.name}</h2>
              <p className="text-sm text-white/40">{place.city}, {place.state?.name || place.state}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6 space-y-6">
            {/* Status */}
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={place.status} />
            </div>

            {/* Description */}
            {place.description && (
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                <h3 className="font-medium text-white mb-2">Description</h3>
                <p className="text-white/60">{place.description}</p>
              </div>
            )}

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {place.address && (
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                  <h3 className="font-medium text-white mb-2">Address</h3>
                  <p className="text-white/60">{place.address}</p>
                </div>
              )}
              {place.map_link && (
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                  <h3 className="font-medium text-white mb-2">Map</h3>
                  <a
                    href={place.map_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 font-medium transition-colors"
                  >
                    View on Google Maps
                    <span className="material-symbols-outlined text-sm">open_in_new</span>
                  </a>
                </div>
              )}
            </div>

            {/* Images */}
            {images.length > 0 && (
              <div>
                <h3 className="font-medium text-white mb-3">Images ({images.length})</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {images.map((img, idx) => (
                    <div key={idx} className="aspect-video rounded-xl overflow-hidden bg-white/5">
                      <img
                        src={img.image_url || img}
                        alt={`${place.name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Information */}
            {keyInfo.length > 0 && (
              <div>
                <h3 className="font-medium text-white mb-3">Key Information</h3>
                <div className="space-y-3">
                  {keyInfo.map((info, idx) => (
                    <div key={idx} className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
                      <h4 className="font-medium text-white mb-1">{info.question}</h4>
                      <p className="text-white/60">{info.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-[#0a0a0f] border-t border-white/5 px-6 py-4 flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors">
              Close
            </button>
            <button
              onClick={() => {
                onClose();
                onEdit(place.id);
              }}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-500 hover:to-indigo-500 transition-all flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">edit</span>
              Edit Place
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// State View Modal
const StateViewModal = ({ isOpen, onClose, stateName, places, onPreview, onDelete, navigate }) => {
  if (!isOpen || !places) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#0a0a0f] border border-white/10 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden animate-fade-in">
          
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-2xl text-white">location_on</span>
              </div>
              <div>
                <h2 className="text-xl font-medium text-white">{stateName}</h2>
                <p className="text-purple-100">{places.length} place{places.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined text-xl">close</span>
            </button>
          </div>

          {/* Grid Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {places.map((place) => {
                const images = place.images || [];
                return (
                  <div
                    key={place.id}
                    className="group bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all duration-300"
                  >
                    {/* Image */}
                    <div 
                      className="aspect-[4/3] relative cursor-pointer overflow-hidden"
                      onClick={() => onPreview(place)}
                    >
                      {images.length > 0 ? (
                        <img
                          src={images[0].image_url || images[0]}
                          alt={place.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500/10 to-indigo-500/10">
                          <span className="material-symbols-outlined text-4xl text-purple-400/50">image</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <span className="material-symbols-outlined text-3xl text-white opacity-0 group-hover:opacity-100 transition-opacity">visibility</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="font-medium text-white text-sm line-clamp-1 mb-1">{place.name}</h3>
                      <p className="text-xs text-white/40 mb-3">{place.city}</p>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onPreview(place)}
                          className="flex-1 px-3 py-1.5 text-xs font-medium text-purple-400 bg-purple-500/10 rounded-lg hover:bg-purple-500/20 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <span className="material-symbols-outlined text-sm">visibility</span>
                          View
                        </button>
                        <button
                          onClick={() => {
                            onClose();
                            navigate(`/places/edit/${place.id}`);
                          }}
                          className="p-1.5 text-white/40 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                        >
                          <span className="material-symbols-outlined text-lg">edit</span>
                        </button>
                        <button
                          onClick={() => onDelete(place)}
                          className="p-1.5 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function PlacesList() {
  const navigate = useNavigate();
  const [places, setPlaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, place: null });
  const [previewModal, setPreviewModal] = useState({ isOpen: false, place: null });
  const [stateViewModal, setStateViewModal] = useState({ isOpen: false, stateName: '', places: [] });

  const fetchPlaces = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/places`);
      setPlaces(response.data || []);
    } catch (error) {
      console.error('Error fetching places:', error);
      toast.error('Failed to load places');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  const handleDelete = async () => {
    if (!deleteModal.place) return;

    try {
      setIsDeleting(true);
      await deletePlace(deleteModal.place.id);
      toast.success('Place deleted successfully');
      setDeleteModal({ isOpen: false, place: null });
      fetchPlaces();
    } catch (error) {
      console.error('Error deleting place:', error);
      toast.error(error.response?.data?.message || 'Failed to delete place');
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter places by search term
  const filteredPlaces = places.filter(place => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      place.name?.toLowerCase().includes(search) ||
      place.city?.toLowerCase().includes(search) ||
      (place.state?.name || place.state || '').toLowerCase().includes(search)
    );
  });

  // Group places by state (normalized)
  const placesByState = filteredPlaces.reduce((acc, place) => {
    const rawStateName = place.state?.name || place.state || 'Unknown';
    const normalizedKey = rawStateName.trim().toLowerCase();
    
    if (!acc[normalizedKey]) {
      const displayName = rawStateName.trim().split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      acc[normalizedKey] = { displayName, places: [] };
    }
    acc[normalizedKey].places.push(place);
    return acc;
  }, {});

  // Sort states by count
  const stateStats = Object.values(placesByState)
    .map(({ displayName, places: statePlaces }) => {
      const sortedPlaces = [...statePlaces].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return {
        name: displayName,
        count: statePlaces.length,
        recentPlaces: sortedPlaces.slice(0, 5),
        allPlaces: sortedPlaces
      };
    })
    .sort((a, b) => b.count - a.count);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-indigo-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      
      {/* ===== HEADER ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extralight tracking-tight text-white">
            Places<span className="text-purple-400 font-bold">.</span>
          </h1>
          <p className="text-sm text-white/40 mt-1">Manage destinations organized by state</p>
        </div>
        <Link
          to="/places/new"
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-sm font-medium rounded-xl transition-all shadow-lg shadow-purple-500/25"
        >
          <span className="material-symbols-outlined text-lg">add</span>
          Add Place
        </Link>
      </div>

      {/* ===== STATS ===== */}
      <div className="grid grid-cols-3 gap-4">
        <div className="group relative bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5 hover:border-purple-500/30 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-xl text-purple-400">location_on</span>
            </div>
            <div className="flex-1">
              <p className="text-2xl font-light text-white">{places.length}</p>
              <p className="text-xs text-white/40">Total Places</p>
            </div>
          </div>
        </div>

        <div className="group relative bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5 hover:border-indigo-500/30 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-indigo-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-xl text-indigo-400">public</span>
            </div>
            <div className="flex-1">
              <p className="text-2xl font-light text-white">{stateStats.length}</p>
              <p className="text-xs text-white/40">States Covered</p>
            </div>
          </div>
        </div>

        <div className="group relative bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-5 hover:border-green-500/30 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-green-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-xl text-green-400">check_circle</span>
            </div>
            <div className="flex-1">
              <p className="text-2xl font-light text-white">
                {places.filter(p => p.status === 'published').length}
              </p>
              <p className="text-xs text-white/40">Published</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== SEARCH ===== */}
      <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-4">
        <div className="relative max-w-md">
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-white/30">search</span>
          <input
            type="text"
            placeholder="Search places by name, city, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 transition-all"
          />
        </div>
      </div>

      {/* ===== PLACES BY STATE ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stateStats.map((state, index) => (
          <div
            key={state.name}
            className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl overflow-hidden hover:border-purple-500/20 transition-all duration-300"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            {/* State Header */}
            <div className="bg-gradient-to-r from-purple-600/20 to-indigo-600/20 px-5 py-4 border-b border-white/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <span className="material-symbols-outlined text-xl text-purple-400">location_on</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{state.name}</h3>
                    <p className="text-purple-300/60 text-sm">{state.count} place{state.count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="text-3xl font-light text-purple-400">{state.count}</div>
              </div>
            </div>

            {/* Recent Places List */}
            <div className="divide-y divide-white/5">
              {state.recentPlaces.map((place) => {
                const images = place.images || [];
                return (
                  <div
                    key={place.id}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/[0.02] transition-colors group"
                  >
                    <div 
                      className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      onClick={() => setPreviewModal({ isOpen: true, place })}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                        {images.length > 0 ? (
                          <img
                            src={images[0].image_url || images[0]}
                            alt={place.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="material-symbols-outlined text-white/20">image</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white text-sm truncate">{place.name}</p>
                        <p className="text-xs text-white/40">{place.city}</p>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setPreviewModal({ isOpen: true, place })}
                        className="p-1.5 text-white/40 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                      >
                        <span className="material-symbols-outlined text-lg">visibility</span>
                      </button>
                      <button
                        onClick={() => navigate(`/places/edit/${place.id}`)}
                        className="p-1.5 text-white/40 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                      >
                        <span className="material-symbols-outlined text-lg">edit</span>
                      </button>
                      <button
                        onClick={() => setDeleteModal({ isOpen: true, place })}
                        className="p-1.5 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <span className="material-symbols-outlined text-lg">delete</span>
                      </button>
                    </div>
                  </div>
                );
              })}
              
              {state.recentPlaces.length === 0 && (
                <div className="px-5 py-4 text-center text-sm text-white/40">
                  No places yet
                </div>
              )}
            </div>
            
            {/* View All Button */}
            <div className="px-5 py-3 bg-white/[0.01] border-t border-white/5 flex items-center justify-between">
              {state.count > 5 && (
                <p className="text-xs text-white/30">
                  Showing 5 of {state.count} places
                </p>
              )}
              <button
                onClick={() => setStateViewModal({ 
                  isOpen: true, 
                  stateName: state.name, 
                  places: state.allPlaces 
                })}
                className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-400 bg-purple-500/10 rounded-lg hover:bg-purple-500/20 transition-colors"
              >
                <span className="material-symbols-outlined text-lg">grid_view</span>
                View All
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {stateStats.length === 0 && (
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
            <span className="material-symbols-outlined text-3xl text-white/20">public</span>
          </div>
          <p className="font-medium text-white mb-1">
            {searchTerm ? 'No places found' : 'No places yet'}
          </p>
          <p className="text-white/40 text-sm mb-4">
            {searchTerm ? 'Try a different search term' : 'Add your first destination to get started'}
          </p>
          {!searchTerm && (
            <Link
              to="/places/new"
              className="inline-flex items-center gap-2 px-4 py-2 text-purple-400 bg-purple-500/10 rounded-lg font-medium hover:bg-purple-500/20 transition-colors"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              Add Place
            </Link>
          )}
        </div>
      )}

      {/* Modals */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, place: null })}
        onConfirm={handleDelete}
        placeName={deleteModal.place?.name}
        isLoading={isDeleting}
      />

      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, place: null })}
        place={previewModal.place}
        onEdit={(id) => navigate(`/places/edit/${id}`)}
      />

      <StateViewModal
        isOpen={stateViewModal.isOpen}
        onClose={() => setStateViewModal({ isOpen: false, stateName: '', places: [] })}
        stateName={stateViewModal.stateName}
        places={stateViewModal.places}
        onPreview={(place) => {
          setStateViewModal({ isOpen: false, stateName: '', places: [] });
          setPreviewModal({ isOpen: true, place });
        }}
        onDelete={(place) => {
          setStateViewModal({ isOpen: false, stateName: '', places: [] });
          setDeleteModal({ isOpen: true, place });
        }}
        navigate={navigate}
      />
    </div>
  );
}

export default PlacesList;
